/* Wallet · aiuti per mostrare dati di catena senza mai esporre un segreto.
 *
 * Perché un file a parte: la schermata dei wallet è l'unica che tocca denaro
 * vero, e le due funzioni che decidono COSA di un wallet finisce a schermo
 * devono stare in un posto solo. Se la troncatura dell'indirizzo fosse scritta
 * in linea in tre componenti, prima o poi uno dei tre stamperebbe il valore
 * intero.
 *
 * Regola che questo file fa rispettare: dal wallet escono solo l'indirizzo
 * pubblico troncato e i saldi. Mai `encryptedPrivateKey`, mai `privateKey`,
 * mai `mnemonic` — nemmeno nella console, nemmeno in localStorage, nemmeno in
 * una query string.
 */

import type { SaldiCatena, Wallet } from '../../api/tipi';

/** I soli campi di un wallet che questa schermata ha il diritto di conoscere.
 *  È una proiezione deliberata: un componente che riceve `WalletPubblico` non
 *  PUÒ stampare la chiave cifrata, perché non ce l'ha. */
export interface WalletPubblico {
  id: number;
  nome: string;
  indirizzo: string | null;
}

/** Campi chiesti a `GET /wallets`: senza `encryptedPrivateKey`, che il CRUD
 *  restituirebbe (non ha `@Exclude()`). Chiederli esplicitamente fa sì che la
 *  chiave cifrata non arrivi nemmeno al browser, invece di arrivarci e non
 *  essere mostrata. */
export const CAMPI_WALLET_PUBBLICI = ['id', 'name', 'address'];

export function soloDatiPubblici(w: Wallet): WalletPubblico {
  return { id: w.id, nome: w.name, indirizzo: w.address };
}

/**
 * Indirizzo troncato. È l'UNICA forma in cui un indirizzo compare a video:
 * basta a riconoscere il wallet giusto e non basta a trascriverlo per sbaglio
 * in una chat o in uno screenshot.
 */
export function troncaIndirizzo(indirizzo: string | null | undefined): string {
  if (!indirizzo) return 'nessun indirizzo';
  const a = indirizzo.trim();
  if (a.length <= 13) return a;
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

/**
 * Saldo di un token. Il backend lo manda come stringa a 18 decimali: mostrarli
 * tutti renderebbe illeggibile la riga, e convertirlo con `Number` senza
 * arrotondare stamperebbe la coda di rumore del virgola mobile.
 * Il valore esatto resta quello della catena: questo è solo il modo di leggerlo.
 */
export function importoToken(grezzo: string | null | undefined): string {
  if (grezzo == null) return '—';
  const v = Number(grezzo);
  if (!Number.isFinite(v)) return '—';
  /* Sotto lo 0,0001 quattro decimali direbbero "0": per le monete di gas è
     proprio la differenza fra "posso operare" e "non posso". */
  const decimali = v !== 0 && Math.abs(v) < 0.0001 ? 8 : 4;
  return v.toLocaleString('it-IT', {
    useGrouping: 'always',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimali,
  });
}

/** Copia negli appunti senza passare da un campo visibile né da un log.
 *  L'indirizzo è pubblico: è l'unica cosa del wallet che si può copiare. */
export async function copiaIndirizzo(indirizzo: string | null): Promise<boolean> {
  if (!indirizzo || !navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(indirizzo);
    return true;
  } catch {
    /* Nessun `console.error` con l'indirizzo dentro: l'errore non lo contiene
       e non deve contenerlo. */
    return false;
  }
}

/** Link all'esploratore della catena. Solo l'indirizzo pubblico finisce
 *  nell'URL, mai altro. */
export function urlEsploratore(catena: SaldiCatena, indirizzo: string | null): string | null {
  if (!indirizzo || !catena.explorerUrl) return null;
  return `${catena.explorerUrl.replace(/\/$/, '')}/address/${indirizzo}`;
}

/** Le catene in cui il wallet ha davvero qualcosa, prima delle altre: chi apre
 *  la schermata cerca dove sono i soldi, non l'ordine dei chainId. */
export function ordinaCatene(catene: SaldiCatena[]): SaldiCatena[] {
  return [...catene].sort((a, b) => (b.totalUsd ?? -1) - (a.totalUsd ?? -1));
}
